import React, { FC, useEffect } from "react";
import { connect } from "react-redux";

import { faRefresh } from "@fortawesome/pro-solid-svg-icons/faRefresh";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { showModal } from "@mapmycustomers/ui";

import { isRefreshing } from "@app/store/pinLegends";
import { refreshPinLegend } from "@app/store/pinLegends/actions";
import { RootState } from "@app/store/rootReducer";

const messages = defineMessages({
  message: {
    id: "map.colorization.createEditSidebar.error.conflict.message",
    defaultMessage:
      "Another user has edited this pin legend and saved changes just now. Please refresh to see changes reflected and continue editing.",
    description: "A message to display on the conflict modal",
  },
  refresh: {
    id: "map.colorization.createEditSidebar.error.conflict.refresh",
    defaultMessage: "{icon} Refresh",
    description: "A refresh button title on the conflict modal",
  },
  title: {
    id: "map.colorization.createEditSidebar.error.conflict",
    defaultMessage: "Changes to the “{name}” pin legend have just been saved by another user",
    description: "A title to display on the conflict modal",
  },
});

export interface ConflictModalProps {
  id: PinLegend["id"];
  loading?: boolean;
  name: PinLegend["name"];
  onHide: () => void;
  onRefreshPinLegend?: typeof refreshPinLegend.request;
  onResetState?: (pinLegend: PinLegend) => void;
}

const ConflictModal: FC<ConflictModalProps> = ({
  id,
  name,
  onHide,
  onRefreshPinLegend,
  onResetState,
}) => {
  const intl = useIntl();

  useEffect(() => {
    const modal = showModal({
      closable: false,
      content: intl.formatMessage(messages.message),
      okText: intl.formatMessage(messages.refresh, {
        icon: <FontAwesomeIcon icon={faRefresh} />,
      }),
      onCancel: () => {
        onHide();
      },
      onOk() {
        return new Promise<void>((resolve) => {
          onRefreshPinLegend?.({
            id,
            callback: (pinLegend: PinLegend) => {
              onResetState?.(pinLegend);
              resolve();
              onHide();
            },
          });
          // show loading state via spinning icon, not via usual loading state
          modal.update({
            okButtonProps: { loading: false },
            okText: intl.formatMessage(messages.refresh, {
              icon: <FontAwesomeIcon icon={faRefresh} spin />,
            }),
          });
        });
      },
      title: intl.formatMessage(messages.title, { name }),
      type: "confirm",
    });
    return () => {
      modal.destroy();
    };
  }, [onRefreshPinLegend, onHide, name, id, onResetState, intl]);

  return null;
};

const mapStateToProps = (state: RootState) => ({
  loading: isRefreshing(state),
});

const mapDispatchToProps = {
  onRefreshPinLegend: refreshPinLegend.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConflictModal);
